<template>
  <div class="relative inline-block">
    <button
      @click="toggleMenu"
      class="border-none rounded cursor-pointer"
      ref="triggerElement"
    >
      <slot name="trigger">Menu</slot>
    </button>

    <teleport :to="teleportTarget" :disabled="!isMounted">
      <div
        v-if="isOpen"
        :class="[
          'absolute bg-white border border-gray-300 rounded-xl shadow-lg p-3 min-w-[50px]', // bubble-menu base styles
          positionClass, // For tail positioning
        ]"
        :style="menuStyles"
        ref="menuElement"
      >
        <slot></slot>
      </div>
    </teleport>
  </div>
</template>

<script>
const TAIL_SIZE = 10; // Module-level constant

export default {
  name: "BubbleMenu",
  props: {
    position: {
      type: String,
      default: "bottom", // Accepted values: 'top', 'bottom', 'start', 'end'
      validator: (value) => ["top", "bottom", "start", "end"].includes(value),
    },
    teleportTarget: {
      type: String,
      default: "body",
    },
    offset: {
      type: Number,
      default: 0,
    },
    closeOnMenuClick: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["opened", "closed"],
  data() {
    return {
      isOpen: false,
      menuStyles: {},
      isMounted: false,
    };
  },
  computed: {
    physicalPosition() {
      const { position } = this; // Access prop
      const dir = this.docDirection;
      if (position === "start") return dir === "rtl" ? "right" : "left";
      if (position === "end") return dir === "rtl" ? "left" : "right";
      return position;
    },
    positionClass() {
      // This class is now primarily for the pseudo-element tail styles
      return `bubble-menu--${this.physicalPosition}`;
    },
    docDirection() {
      // Assuming Vuetify 3 for locale. If using Vue I18n or other, adjust accordingly.
      // Placeholder if $vuetify is not available or you use a different i18n solution.
      if (this.$vuetify && this.$vuetify.locale) {
        return this.$vuetify.locale.current === "ar" ? "rtl" : "ltr";
      }
      // Fallback or alternative logic for direction
      // For example, checking document.documentElement.dir
      return document.documentElement.dir === "rtl" ? "rtl" : "ltr";
    },
  },
  watch: {
    isOpen(newValue) {
      if (newValue) {
        this.$nextTick(this.updateMenuPosition);
      }
    },
    position() {
      if (this.isOpen) this.updateMenuPosition();
    },
    offset() {
      if (this.isOpen) this.updateMenuPosition();
    },
    docDirection() {
      if (this.isOpen) this.updateMenuPosition();
    },
  },
  methods: {
    async updateMenuPosition() {
      if (!this.isOpen || !this.$refs.triggerElement) return;
      await this.$nextTick();
      if (!this.$refs.menuElement) return;

      const triggerRect = this.$refs.triggerElement.getBoundingClientRect();
      const menuWidth = this.$refs.menuElement.offsetWidth;
      const menuHeight = this.$refs.menuElement.offsetHeight;
      const totalOffset = TAIL_SIZE + this.offset;

      let top, left;
      const currentPhysicalPosition = this.physicalPosition;

      switch (currentPhysicalPosition) {
        case "top":
          top = triggerRect.top - menuHeight - totalOffset + window.scrollY;
          left =
            triggerRect.left +
            triggerRect.width / 2 -
            menuWidth / 2 +
            window.scrollX;
          break;
        case "left":
          top =
            triggerRect.top +
            triggerRect.height / 2 -
            menuHeight / 2 +
            window.scrollY;
          left = triggerRect.left - menuWidth - totalOffset + window.scrollX;
          break;
        case "right":
          top =
            triggerRect.top +
            triggerRect.height / 2 -
            menuHeight / 2 +
            window.scrollY;
          left = triggerRect.right + totalOffset + window.scrollX;
          break;
        case "bottom":
        default:
          top = triggerRect.bottom + totalOffset + window.scrollY;
          left =
            triggerRect.left +
            triggerRect.width / 2 -
            menuWidth / 2 +
            window.scrollX;
          break;
      }

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const scrollbarSize = 15; // Consider if this is always accurate

      // Adjust for viewport boundaries
      if (left + menuWidth > viewportWidth - scrollbarSize) {
        left = viewportWidth - menuWidth - scrollbarSize;
      }
      if (left < TAIL_SIZE) { // Ensure tail is not cut off on the left
        left = TAIL_SIZE;
      }
      if (top + menuHeight > viewportHeight - TAIL_SIZE + window.scrollY) {
        top = viewportHeight - menuHeight - TAIL_SIZE + window.scrollY;
      }
      if (top < TAIL_SIZE + window.scrollY) { // Ensure tail is not cut off on the top
        top = TAIL_SIZE + window.scrollY;
      }

      this.menuStyles = {
        // position: "absolute", // Now handled by Tailwind class 'absolute'
        top: `${top}px`,
        left: `${left}px`,
        zIndex: 1001, // Or use Tailwind's z-index utilities if preferred, e.g., z-[1001]
      };
    },
    toggleMenu() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.$emit("opened");
        this.$nextTick(this.updateMenuPosition);
      } else {
        this.$emit("closed");
      }
    },
    closeMenuOnClickOutside(event) {
      if (!this.isOpen) return;
      const isClickOnTrigger =
        this.$refs.triggerElement &&
        this.$refs.triggerElement.contains(event.target);
      const isClickOnMenu =
        this.$refs.menuElement && this.$refs.menuElement.contains(event.target);

      if (!isClickOnTrigger && !isClickOnMenu) {
        this.isOpen = false;
        this.$emit("closed");
      } else if (isClickOnMenu && this.closeOnMenuClick) {
        this.isOpen = false;
        this.$emit("closed");
      }
    },
    handleResizeOrScroll() {
      if (this.isOpen) {
        this.updateMenuPosition();
      }
    },
  },
  mounted() {
    this.isMounted = true;
    document.addEventListener("click", this.closeMenuOnClickOutside, true);
    window.addEventListener("resize", this.handleResizeOrScroll);
    window.addEventListener("scroll", this.handleResizeOrScroll, true);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeMenuOnClickOutside, true);
    window.removeEventListener("resize", this.handleResizeOrScroll);
    window.removeEventListener("scroll", this.handleResizeOrScroll, true);
  },
};
</script>

<style scoped>

/* Bubble Tail Styling */
.bubble-menu--bottom::before,
.bubble-menu--bottom::after,
.bubble-menu--top::before,
.bubble-menu--top::after,
.bubble-menu--right::before,
.bubble-menu--right::after,
.bubble-menu--left::before,
.bubble-menu--left::after {
  content: "";
  position: absolute;
  width: 0;
  height: 0;
  border-style: solid;
}

.bubble-menu--bottom::before {
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-width: 0 10px 10px 10px;
  border-color: transparent transparent #cccccc transparent; /* Tailwind: border-b-gray-300 for the visible part */
}
.bubble-menu--bottom::after {
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: -1px; /* Adjusts to overlap border */
  border-width: 0 9px 9px 9px;
  border-color: transparent transparent #ffffff transparent; /* Tailwind: border-b-white for the visible part */
}

.bubble-menu--top::before {
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-width: 10px 10px 0 10px;
  border-color: #cccccc transparent transparent transparent; /* Tailwind: border-t-gray-300 */
}
.bubble-menu--top::after {
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: -1px;
  border-width: 9px 9px 0 9px;
  border-color: #ffffff transparent transparent transparent; /* Tailwind: border-t-white */
}

.bubble-menu--right::before {
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
  border-width: 10px 10px 10px 0;
  border-color: transparent #cccccc transparent transparent; /* Tailwind: border-r-gray-300 */
}
.bubble-menu--right::after {
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-right: -1px;
  border-width: 9px 9px 9px 0;
  border-color: transparent #ffffff transparent transparent; /* Tailwind: border-r-white */
}

.bubble-menu--left::before {
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  border-width: 10px 0 10px 10px;
  border-color: transparent transparent transparent #cccccc; /* Tailwind: border-l-gray-300 */
}
.bubble-menu--left::after {
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-left: -1px;
  border-width: 9px 0 9px 9px;
  border-color: transparent transparent transparent #ffffff; /* Tailwind: border-l-white */
}
</style>